import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  Box, TextField, Button, Typography, CircularProgress,
  Alert, InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid username or password';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#0a0a0a',
    }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: 360,
          bgcolor: '#111',
          border: '1px solid #1e1e1e',
          borderRadius: 0,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* Logo / wordmark */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography sx={{
            fontSize: 22, fontWeight: 'bold', letterSpacing: 4,
            color: 'primary.main', fontFamily: 'monospace',
          }}>
            ◈ KAIROS
          </Typography>
          <Typography sx={{ fontSize: 10, color: '#444', letterSpacing: 2, mt: 0.5 }}>
            MEDICAL IMAGING PLATFORM
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ bgcolor: '#1a0000', border: '1px solid #440000', borderRadius: 0, fontSize: 12 }}
          >
            {error}
          </Alert>
        )}

        <TextField
          label="Username or Email"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
          autoFocus
          autoComplete="username"
          size="small"
          InputLabelProps={{ sx: { fontSize: 12, color: '#555' } }}
          InputProps={{ sx: { fontSize: 13, fontFamily: 'monospace', borderRadius: 0 } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#2a2a2a' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
          }}
        />

        <TextField
          label="Password"
          type={showPw ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          size="small"
          InputLabelProps={{ sx: { fontSize: 12, color: '#555' } }}
          InputProps={{
            sx: { fontSize: 13, fontFamily: 'monospace', borderRadius: 0 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPw(p => !p)}
                  sx={{ color: '#555' }}
                >
                  {showPw ? <VisibilityOffIcon sx={{ fontSize: 16 }} />
                           : <VisibilityIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#2a2a2a' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={loading || !username || !password}
          sx={{
            mt: 0.5,
            borderRadius: 0,
            bgcolor: '#003344',
            color: 'primary.main',
            border: '1px solid #00bcd4',
            fontFamily: 'monospace',
            letterSpacing: 2,
            fontSize: 12,
            py: 1,
            '&:hover': { bgcolor: '#004455' },
            '&.Mui-disabled': { bgcolor: '#111', color: '#333', borderColor: '#222' },
          }}
        >
          {loading ? <CircularProgress size={16} sx={{ color: 'primary.main' }} /> : 'SIGN IN'}
        </Button>

        <Typography sx={{ fontSize: 9, color: '#333', textAlign: 'center', letterSpacing: 1 }}>
          AUTHORIZED USERS ONLY · PHI ACCESS LOGGED
        </Typography>
      </Box>
    </Box>
  );
}
